import { SlidersHorizontal } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import type { WorkExperienceGroupMode } from '../../utils/workExperienceGroup'
import type { WorkExperienceSortMode } from '../../utils/workExperienceSort'

type WorkExperienceSectionMenuProps = {
  sortMode: WorkExperienceSortMode
  groupMode: WorkExperienceGroupMode
  onSortModeChange: (mode: WorkExperienceSortMode) => void
  onGroupModeChange: (mode: WorkExperienceGroupMode) => void
}

const menuItemClass = (active: boolean) =>
  `w-full px-3 py-2 text-left text-sm transition-colors ${
    active ? 'bg-brand-primary-subtle text-brand-primary font-medium' : 'text-text-secondary hover:bg-surface-muted'
  }`

export default function WorkExperienceSectionMenu({
  sortMode,
  groupMode,
  onSortModeChange,
  onGroupModeChange,
}: WorkExperienceSectionMenuProps) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return

    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [open])

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="p-1 text-text-muted hover:text-text-primary rounded hover:bg-surface-muted"
        title="More actions"
        aria-label="Work experience options"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <SlidersHorizontal className="w-4 h-4" />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-20 mt-1 w-56 rounded-md border border-border-default bg-surface-default py-1 shadow-lg"
        >
          <div className="px-3 py-1.5 text-xs font-medium text-text-muted">Sort</div>
          <button
            type="button"
            role="menuitemradio"
            aria-checked={sortMode === 'manual'}
            onClick={() => {
              if (sortMode !== 'manual') onSortModeChange('manual')
              setOpen(false)
            }}
            className={menuItemClass(sortMode === 'manual')}
          >
            Manual
          </button>
          <button
            type="button"
            role="menuitemradio"
            aria-checked={sortMode === 'by_date'}
            onClick={() => {
              if (sortMode !== 'by_date') onSortModeChange('by_date')
              setOpen(false)
            }}
            className={menuItemClass(sortMode === 'by_date')}
          >
            By date
          </button>

          <div className="my-1 border-t border-border-default" />

          <div className="px-3 py-1.5 text-xs font-medium text-text-muted">Group by</div>
          <button
            type="button"
            role="menuitemradio"
            aria-checked={groupMode === 'company'}
            onClick={() => {
              if (groupMode !== 'company') onGroupModeChange('company')
            }}
            className={menuItemClass(groupMode === 'company')}
          >
            Company
          </button>
          <button
            type="button"
            role="menuitemradio"
            aria-checked={groupMode === 'position'}
            onClick={() => {
              if (groupMode !== 'position') onGroupModeChange('position')
            }}
            className={menuItemClass(groupMode === 'position')}
          >
            Position
          </button>
        </div>
      )}
    </div>
  )
}
